<template>
	<div class="mark-buttons">
		<div class="mark-buttons__row">
			<TagButton
				:enabled="markInEnabled"
				:highlighted="markInEnabled"
				:active="markingActive"
				:label="labels.markIn"
				class="mark-buttons__button"
				:class="{ '-active': markingActive }"
				icon-name="pointing-left"
				icon-size="lg"
				:is-full-width="true"
				:label-shadow="false"
				@click="onMarkInClick()"
			/>
			<TagButton
				:enabled="markOutEnabled"
				:highlighted="markOutEnabled"
				:active="false"
				:label="labels.markOut"
				class="mark-buttons__button -out"
				:class="{ '-highlighted': markOutEnabled }"
				icon-name="pointing-right"
				icon-size="lg"
				icon-position="after"
				:label-shadow="false"
				@click="onMarkOutClick()"
			>
				<template #label>
					<div>{{ labels.markOut }}</div>
					<div
						v-if="isMarkingEvent"
						class="mark-buttons__duration"
					>
						{{ durationFormatted }}
					</div>
				</template>
			</TagButton>
		</div>
		<div
			v-if="isMarkingEvent"
			class="mark-buttons__footer"
		>
			<button
				class="mark-buttons__cancel"
				@click="onCancel"
			>
				{{ labels.cancel }}
			</button>
			<div
				v-if="warning"
				class="mark-buttons__warning"
			>
				<Icon
					icon-name="c-info"
					size="md"
				/>
				{{ warning }}
			</div>
		</div>
	</div>
</template>

<script>
import TagButton from '@videotools/js/one/components/TagButton/TagButton';
import transHelpers from '@videotools/js/util/transHelpers';
import { TAG_NAME_CUT } from '@videotools/js/api/entities/TagButton';
import { mapActions, mapGetters } from 'vuex';
import bus, { EVENT } from '@videotools/js/events';
import { formatTimestampAsMinutesSeconds } from '@shared/utils/timeUtils';

const DEFAULT_CLIP_NAME = `${transHelpers.transTagLabel(TAG_NAME_CUT)}`;
const events = [EVENT.PLAYER.ON.PROGRESS];

export default {
	name:       'MarkInOutButtons',
	components: { TagButton },
	props:      {
		clipName: {
			type:    String,
			default: DEFAULT_CLIP_NAME,
		},
		templateData: {
			type:    Object,
			default: null,
		},
	},
	data() {
		return {
			elapsed: -1,
			start:   -1,
		};
	},
	computed: {
		...mapGetters({
			isAppleDevice:    'device/isAppleDevice',
			markingActive:    'markingZone/active',
			markingZoneStart: 'markingZone/start',
			isMarkingEvent:   'markingZone/isMarkingEvent',
			event:            'event/event',
			cutTag:           'tags/cutTag',
		}),
		labels() {
			return {
				markIn:  this.$I18n.trans('one.mark_in'),
				markOut: this.$I18n.trans('one.mark_out'),
				cancel:  this.transUcfirst('commons.cancel'),
			};
		},
		markInEnabled() {
			return !this.markingActive;
		},
		markOutEnabled() {
			return this.isMarkingEvent && this.markingActive && !this.warning;
		},
		duration() {
			const duration = this.elapsed - this.markingZoneStart;
			return duration > 0 ? duration : 0;
		},
		durationFormatted() {
			return formatTimestampAsMinutesSeconds(this.duration);
		},
		warning() {
			if (!this.isMarkingEvent) {
				return null;
			}
			if (this.markingActive && this.markingZoneStart > this.elapsed) {
				return this.transUcfirst('one.marking_position_invalid');
			}

			return null;
		},
	},
	created() {
		// subscribe to external events
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}
	},
	destroyed() {
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}
	},
	methods: {
		...mapActions({
			startMarking:   'markingZone/startMarking',
			endMarking:     'markingZone/endMarking',
			cancelMarking:  'markingZone/cancel',
			createEventTag: 'event/createEventTag',
		}),
		[`BUS-${EVENT.PLAYER.ON.PROGRESS}`](elapsedSeconds) {
			this.elapsed = elapsedSeconds;
		},
		onMarkInClick() {
			this.start = this.elapsed;
			this.startMarking({ position: this.elapsed, tag: this.cutTag });
		},
		onMarkOutClick() {
			this.endMarking(this.elapsed);
			this.createCut(this.cutTag, this.start, this.duration);
			this.start = -1;
		},
		onCancel() {
			this.cancelMarking();
		},
		async createCut(eventTag, timestamp, duration, participantId = null) {
			await this.createEventTag({
				data: {
					eventId:           this.event.id,
					tagId:             eventTag.tag.id,
					participantId,
					timestamp:         Math.round(timestamp),
					secondsBefore:     0,
					secondsAfter:      Math.round(duration),
					timeline:          eventTag.timeline,
					taggingToolOnly:   eventTag.taggingToolOnly,
					priority:          eventTag.priority,
					highlight:         eventTag.highlight,
					name:              this.clipName,
					tagTemplateItemId: this.templateData?.id,
				},
				backendSnap: this.isAppleDevice,
				absolute:    true,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';
@import "@videotools/sass/_fonts.scss";

.mark-buttons {
	width: 100%;
}

.mark-buttons__row {
	display: flex;
	justify-content: space-between;
}

.mark-buttons__button {
	padding: 16px 20px;

	.is-mobile & {
		padding: 16px 20px;
		max-width: 163px;
		height: 80px;
	}

	&.-out {
		padding-top: 0;
		padding-bottom: 0;

		&.-highlighted {
			background: var(--color-red);
			color: var(--color-white);

			&:hover,
			&:focus {
				background: var(--color-red);
			}
		}
	}
}

.mark-buttons__duration {
	@include text-sm-2;
}

.mark-buttons__footer {
	margin-top: 16px;
	display: flex;
}

.mark-buttons__warning {
	margin-left: 16px;
	display: flex;
	align-items: center;
	@include text-sm-2;
	color: var(--color-grey-300);

	.v2-icon {
		margin-right: 8px;
	}
}

.mark-buttons__cancel {
	@include text-sm-2;
	display: flex;
	justify-content: center;
	align-items: center;
	min-width: 78px;
	height: 32px;
	padding: 0 16px;
	color: var(--color-soft-white);
	background: none;
	border: 1px solid var(--color-soft-white);
	border-radius: 2px;
	cursor: pointer;

	&:hover,
	&:focus {
		color: var(--color-white);
		border-color: var(--color-white);
	}
}
</style>
